import styled from 'styled-components';
import { Button } from '@shared/ui/Button';
import { Badge } from '@shared/ui/Badge';
import { Loader } from '@shared/ui/Progress';
import { IconClipboardCheck, IconFile, IconArrowRight } from '@shared/ui/icons';
import { useLazyGetCounterpartyRequisiteCheckQuery } from '@shared/api';
import {
  requisiteFieldLabel,
  type RequisiteChanges,
  type RequisiteDiscrepancy,
} from '@entities/legal/types';

// — секция «Сверка реквизитов» на карточке контрагента. По кнопке backend
// сверяет реквизиты (директор/адрес/ОГРН…) с загруженными уставными
// документами и возвращает расхождения. Тон — ИНФОРМАЦИОННЫЙ сигнал под
// решение юриста, не автоматический вывод: система лишь подсвечивает
// возможные расхождения, финальную оценку даёт человек.

const Intro = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Body = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

const Empty = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
  font-style: italic;
  line-height: 1.5;
`;

const Hint = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.5;
`;

const ErrorText = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.status.danger};
  line-height: 1.5;
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const Item = styled.li<{ $warn: boolean }>`
  padding: ${({ theme }) => theme.spacing(2.5)} ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-left: 3px solid
    ${({ $warn, theme }) => ($warn ? theme.colors.status.warning : theme.colors.status.info)};
  border-radius: ${({ theme }) => theme.radius.md};
`;

const ItemHead = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const FieldName = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

/* Сравнение значений: две колонки со значением и источником, между ними
   знак ≠. На узких экранах (360px) колонки складываются в одну — знак
   поворачивается вертикально, горизонтального скролла нет. */
const Values = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: stretch;
  gap: ${({ theme }) => theme.spacing(2)};

  @media (max-width: 420px) {
    grid-template-columns: 1fr;
  }
`;

const ValBlock = styled.div`
  min-width: 0;
`;

const Val = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  overflow-wrap: anywhere;
`;

const Src = styled.div`
  margin-top: 2px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Neq = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.secondary};

  @media (max-width: 420px) {
    justify-content: flex-start;
  }
`;

const DocLink = styled.button`
  margin-top: ${({ theme }) => theme.spacing(2)};
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  background: transparent;
  border: none;
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.primary};
  transition: color ${({ theme }) => theme.motion.fast}
    ${({ theme }) => theme.motion.easeOut};

  &:hover {
    color: ${({ theme }) => theme.colors.primaryHover};
  }
`;

const Meta = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Note = styled.p`
  margin: ${({ theme }) => theme.spacing(2)} 0 0;
  font-size: 12px;
  font-style: italic;
  color: ${({ theme }) => theme.colors.text.muted};
  line-height: 1.5;
`;

const BtnIcon = styled.span`
  display: inline-flex;
  align-items: center;
`;

function fmtDateTime(s: string): string {
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? s : d.toLocaleString('ru-RU');
}

function isWarn(d: RequisiteDiscrepancy): boolean {
  return d.severity === 'warning';
}

interface Props {
  counterpartyId: string;
  /** Быстрая отметка из `risk_flags.requisite_changes` — показываем до запуска
   *  сверки, чтобы юрист видел, найдены ли расхождения, ещё не нажимая кнопку. */
  knownChanges?: RequisiteChanges;
  /** Переход к документу-источнику в секции «Документы контрагента». */
  onOpenDocument?: (docId: string) => void;
}

export function RequisiteCheck({ counterpartyId, knownChanges, onOpenDocument }: Props) {
  const [runCheck, { data, isFetching, isError }] =
    useLazyGetCounterpartyRequisiteCheckQuery();

  // Расхождения сортируем «важные вперёд» — то, что требует внимания юриста,
  // не должно теряться ниже информационных.
  const sorted = data
    ? [...data.discrepancies].sort(
        (a, b) => Number(isWarn(b)) - Number(isWarn(a)),
      )
    : [];

  return (
    <div>
      <Intro>
        Сверяет загруженные уставные документы (устав, приказы, доверенности,
        карточка предприятия) с реквизитами контрагента и подсвечивает
        расхождения — например, если директор в приказе не совпадает с ЕГРЮЛ.
      </Intro>

      <Button
        type="button"
        $variant="secondary"
        $size="sm"
        onClick={() => void runCheck(counterpartyId)}
        disabled={isFetching}
      >
        <BtnIcon>
          <IconClipboardCheck size={16} />
        </BtnIcon>
        {isFetching
          ? 'Проверяем…'
          : data
            ? 'Проверить заново'
            : 'Проверить реквизиты по документам'}
      </Button>

      <Body>
        {isFetching && !data ? (
          <Loader />
        ) : isError && !data ? (
          <ErrorText role="alert">
            Не удалось выполнить сверку реквизитов. Попробуйте ещё раз.
          </ErrorText>
        ) : data ? (
          <>
            {sorted.length === 0 ? (
              <Empty>
                {data.documents_analyzed > 0
                  ? 'Расхождений не обнаружено — реквизиты в документах совпадают с карточкой.'
                  : 'Нет обработанных документов. Загрузите уставные документы ниже, чтобы сверить реквизиты.'}
              </Empty>
            ) : (
              <List>
                {sorted.map((d, i) => {
                  const warn = isWarn(d);
                  // Захватываем doc_id в локальную const — тогда обработчик
                  // клика получает `string` без каста (внутри замыкания TS не
                  // сужает d.doc_id).
                  const docId = d.doc_id;
                  return (
                    <Item key={`${d.field}-${docId ?? 'none'}-${i}`} $warn={warn}>
                      <ItemHead>
                        <FieldName>{requisiteFieldLabel(d.field)}</FieldName>
                        <Badge $tone={warn ? 'warning' : 'info'}>
                          {warn ? 'важное' : 'инфо'}
                        </Badge>
                      </ItemHead>
                      <Values>
                        <ValBlock>
                          <Val>{d.value_a || '—'}</Val>
                          <Src>{d.source_a}</Src>
                        </ValBlock>
                        <Neq aria-hidden="true">≠</Neq>
                        <ValBlock>
                          <Val>{d.value_b || '—'}</Val>
                          <Src>{d.source_b}</Src>
                        </ValBlock>
                      </Values>
                      {docId && onOpenDocument && (
                        <DocLink
                          type="button"
                          onClick={() => onOpenDocument(docId)}
                          title="Перейти к документу-источнику"
                        >
                          <IconFile size={13} />
                          Документ-источник
                          <IconArrowRight size={12} />
                        </DocLink>
                      )}
                    </Item>
                  );
                })}
              </List>
            )}
            <Meta>
              Проанализировано документов: {data.documents_analyzed} · последняя
              проверка: {fmtDateTime(data.checked_at)}
            </Meta>
            <Note>
              Система сигнализирует о возможных расхождениях; решение принимает
              юрист.
            </Note>
          </>
        ) : knownChanges?.has_changes ? (
          <Hint>
            По последней проверке найдены расхождения:{' '}
            {knownChanges.warning_count} важных, {knownChanges.info_count}{' '}
            информационных. Нажмите кнопку, чтобы посмотреть детали.
          </Hint>
        ) : knownChanges && knownChanges.documents_analyzed > 0 ? (
          <Hint>
            По последней проверке расхождений не найдено. Можно перепроверить —
            например, после загрузки новых документов.
          </Hint>
        ) : (
          <Hint>
            Нажмите кнопку, чтобы сверить реквизиты контрагента с загруженными
            уставными документами.
          </Hint>
        )}
      </Body>
    </div>
  );
}
